import { getBucketData } from 'lib/utilityFunctions';
import gameIds from '../data/GameIds';
import { persistStateTypes } from '../../reducers/persistStateReducer';
import curiosoState, { curiosoStateForDemo } from './state/curiosoState';
import readBucketData from './bucketsMigration';
import { SEASON, CURIOSO_CUSTOMIZATION_LIMIT } from '../data/constants';

let lockedFeatures = {};
let lastEarnedAugmentation = {
  section: 'head',
  bodyPart: 'default',
};

let currentActiveQuestId = 0;
let demoSeason;

// function to merge two objects when new augmentations are released
const mergeAugmentations = (stateAugmentation, savedAugmentation, sectionName) =>
  ({
    ...stateAugmentation[sectionName],
    ...(savedAugmentation && savedAugmentation[sectionName] ? savedAugmentation[sectionName] : {}),
  });

const curiosoReducer = (state = curiosoState, action) => {
  let metagameBucketData = {};
  let curiosoSelection = {};
  let mergedCuriosoSelection = {};

  // necessary to have default values for teacher deep link
  if (action.type === persistStateTypes.SET_AUTH) {
    const { isStaffLogin } = action.payload;
    return {
      ...state,
      isStaffLogin,
    };
  }

  if (action.gameId !== gameIds.METAGAME) {
    return state;
  }

  switch (action.type) {
    case 'SET_CURIOSO_ATTRIBUTE':
      return {
        ...state,
        curioso: { ...state.curioso, ...action.payload },
        introComplete: true,
      };

    case 'UNLOCK_CURIOSO_AUGMENTATIONS_LIST':
      lockedFeatures = { ...state.lockedFeatures };
      action.payload.list.forEach((elem) => {
        lockedFeatures[elem.type][elem.part] = false;
        lastEarnedAugmentation = { section: elem.type, bodyPart: elem.part };
      });
      return {
        ...state,
        lastEarnedAugmentation,
        lockedFeatures: {
          ...state.lockedFeatures,
          ...lockedFeatures,
        },
      };

    // augmentation is applied to your Curioso
    case 'SET_CURIOSO_AUGMENTATION_SECTION':
      return {
        ...state,
        augmentations: {
          ...state.augmentations,
          [action.payload.sectionName]: action.payload.sectionData,
        },
      };

    case 'SET_INTRO_COMPLETE':
      return {
        ...state,
        introComplete: true,
      };

    case persistStateTypes.REQUESTRESPONSE_GAMESTATE_ALL: {
      const { isStaffLogin } = state;
      let localState = state;
      let newState = {};
      try {
        demoSeason = ((action.stateData[gameIds.TEACHER_DASHBOARD_SETTINGS] || {}).GameData || {}).demoSeason;
        if (demoSeason || isStaffLogin) {
          // override state with hardcoded Demo state and default to season 1 for teacher deep link
          localState = curiosoStateForDemo[demoSeason ?? SEASON.GRADE_K_1];
        }

        metagameBucketData = (
          getBucketData(
            action.stateData[gameIds.METAGAME],
          ).gameDetails || {});

        curiosoSelection = (
          getBucketData(
            action.stateData[gameIds.METAGAME_CURIOSO_SELECTED],
          ).gameDetails || {});

        const { curiosoCustomizationLimit = null } = getBucketData(action.stateData[gameIds.STUDENT_SETTINGS]) || {};

        if (curiosoSelection.introComplete === undefined) {
          mergedCuriosoSelection = { introComplete: localState.introComplete, curioso: { ...localState.curioso } };
        } else {
          mergedCuriosoSelection = {
            introComplete: curiosoSelection.introComplete,
            curioso: { ...curiosoSelection.curioso },
          };
        }
        currentActiveQuestId = metagameBucketData.currentActiveQuestId;

        newState = {
          ...localState,
          curioso: { ...localState.curioso, ...mergedCuriosoSelection.curioso },
          introComplete: currentActiveQuestId !== undefined &&
            (currentActiveQuestId !== 1 && currentActiveQuestId !== 201)
            ? true : mergedCuriosoSelection.introComplete,
          lastEarnedAugmentation: {
            ...localState.lastEarnedAugmentation,
            ...metagameBucketData.lastEarnedAugmentation,
          },
          augmentations: {
            ...localState.augmentations,
            head: mergeAugmentations(
              localState.augmentations,
              readBucketData(
                localState,
                action,
                'curioso_augmentations',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_SELECTED,
              ),
              'head',
            ),
            body: mergeAugmentations(
              localState.augmentations,
              readBucketData(
                localState,
                action,
                'curioso_augmentations',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_SELECTED,
              ),
              'body',
            ),
            back: mergeAugmentations(
              localState.augmentations,
              readBucketData(
                localState,
                action,
                'curioso_augmentations',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_SELECTED,
              ),
              'back',
            ),
            legs: mergeAugmentations(
              localState.augmentations,
              readBucketData(
                localState,
                action,
                'curioso_augmentations',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_SELECTED,
              ),
              'legs',
            ),
            tail: mergeAugmentations(
              localState.augmentations,
              readBucketData(
                localState,
                action,
                'curioso_augmentations',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_SELECTED,
              ),
              'tail',
            ),
            eyes: mergeAugmentations(
              localState.augmentations,
              readBucketData(
                localState,
                action,
                'curioso_augmentations',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_SELECTED,
              ),
              'eyes',
            ),
            face: mergeAugmentations(
              localState.augmentations,
              readBucketData(
                localState,
                action,
                'curioso_augmentations',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_SELECTED,
              ),
              'face',
            ),
          },
          lockedFeatures: {
            ...localState.lockedFeatures,
            head: mergeAugmentations(
              localState.lockedFeatures,
              readBucketData(
                localState,
                action,
                'curioso_lockedFeatures',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_EARNED,
              ),
              'head',
            ),
            body: mergeAugmentations(
              localState.lockedFeatures,
              readBucketData(
                localState,
                action,
                'curioso_lockedFeatures',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_EARNED,
              ),
              'body',
            ),
            back: mergeAugmentations(
              localState.lockedFeatures,
              readBucketData(
                localState,
                action,
                'curioso_lockedFeatures',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_EARNED,
              ),
              'back',
            ),
            legs: mergeAugmentations(
              localState.lockedFeatures,
              readBucketData(
                localState,
                action,
                'curioso_lockedFeatures',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_EARNED,
              ),
              'legs',
            ),
            tail: mergeAugmentations(
              localState.lockedFeatures,
              readBucketData(
                localState,
                action,
                'curioso_lockedFeatures',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_EARNED,
              ),
              'tail',
            ),
            eyes: mergeAugmentations(
              localState.lockedFeatures,
              readBucketData(
                localState,
                action,
                'curioso_lockedFeatures',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_EARNED,
              ),
              'eyes',
            ),
            face: mergeAugmentations(
              localState.lockedFeatures,
              readBucketData(
                localState,
                action,
                'curioso_lockedFeatures',
                gameIds.METAGAME_CURIOSO_AUGMENTATIONS_EARNED,
              ),
              'face',
            ),
          },
          curiosoCustomizationLimit: curiosoCustomizationLimit
            ? Number(curiosoCustomizationLimit)
            : CURIOSO_CUSTOMIZATION_LIMIT,
        };
      } catch (e) {
        // use current state if new state fails
        newState = state;
        // we may consider dispatching throwCustomAquaError here but it is anti-pattern unless we implement asyncDispatch middleware
        // https://stackoverflow.com/questions/36730793/can-i-dispatch-an-action-in-reducer
        console.error(
          `Error found in curiosoReducer ${action.type} action\n`,
          e,
          '\n\n gameState causing error:\n',
          action.stateData,
        );
      }
      return newState;
    }
    default:
      return state;
  }
};

export default curiosoReducer;
